import { useEffect, useRef, useState } from 'react';
import { getErrorMessage } from '../../../../shared/src';
import { api, type AuditExportFormat, type AuditExportScope, type AuditLogFilters } from '../../api';
import {
  EVENTS_PER_FILE,
  ExportStoppedError,
  MAX_EXPORT_EVENTS,
  describeDuration,
  describeSize,
  describeTimeLeft,
  planExport,
  type ExportProgress,
} from '../../lib/auditExport';
import {
  canContinue,
  continueAuditExport,
  runAuditExport,
  type AuditExportRun,
  type AuditExportStopped,
} from '../../lib/runAuditExport';

interface ExportAuditLogsModalProps {
  /** The tab's active filters: what "Selected data" exports */
  filters: AuditLogFilters;
  /** The same filters in words; empty when none is active */
  filterSummary: string[];
  /** How many events the active filters match */
  selectionCount: number;
  timeZone: string;
  onClose: () => void;
  onExported: () => void;
}

type Status =
  | { state: 'idle' }
  | { state: 'running'; progress: ExportProgress | null }
  | { state: 'stopped'; cancelled: boolean; message: string; resumeAt: AuditExportRun | null };

const formatEvents = (n: number) => n.toLocaleString('en-US');

/** Why an export failed, as a sentence: fetch's network TypeError says only "Failed to fetch". */
function failureReason(err: unknown): string {
  const message = err instanceof TypeError ? 'the connection to Fakto was lost' : getErrorMessage(err);
  return /[.!?]$/.test(message) ? message : `${message}.`;
}

/**
 * Export Admin > Audit Logs as CSV or Excel: the whole history, or the events
 * the active filters select (paging and sorting are not filters). The export
 * is itself recorded in the log, as the last file's last line.
 *
 * The dialog says what the chosen export amounts to before it starts
 * (planExport): quietly up to 250,000 events, as a large export up to a
 * million, as several CSV files up to five million, and refuses beyond.
 */
export function ExportAuditLogsModal({
  filters,
  filterSummary,
  selectionCount,
  timeZone,
  onClose,
  onExported,
}: ExportAuditLogsModalProps) {
  const hasFilters = filterSummary.length > 0;
  const [format, setFormat] = useState<AuditExportFormat>('csv');
  const [scope, setScope] = useState<AuditExportScope>(hasFilters ? 'selection' : 'all');
  const [allCount, setAllCount] = useState<number | null>(null);
  const [status, setStatus] = useState<Status>({ state: 'idle' });
  const cancelled = useRef(false);

  useEffect(() => {
    api
      .listAuditLogs({ limit: 1 })
      .then((r) => setAllCount(r.pagination.total))
      .catch(() => setAllCount(null));
  }, []);

  const running = status.state === 'running';
  const count = scope === 'all' ? allCount : selectionCount;
  const plan = count === null ? null : planExport(count, format);
  const chosenFormat: AuditExportFormat = plan?.format ?? format;

  const callbacks = {
    timeZone,
    onProgress: (progress: ExportProgress) => setStatus({ state: 'running', progress }),
    isCancelled: () => cancelled.current,
  };

  const run = async (go: () => Promise<void>) => {
    cancelled.current = false;
    setStatus({ state: 'running', progress: null });
    try {
      await go();
      onExported();
    } catch (err) {
      if (err instanceof ExportStoppedError) {
        const stopped = err as AuditExportStopped;
        setStatus({
          state: 'stopped',
          cancelled: stopped.reason === 'cancelled',
          message: stopped.reason === 'cancelled' ? 'Export cancelled.' : `Export failed: ${failureReason(stopped.cause ?? err)}`,
          resumeAt: stopped.resumeAt,
        });
      } else {
        // Refused or failed before anything was fetched (e.g. too many events)
        setStatus({ state: 'stopped', cancelled: false, message: getErrorMessage(err), resumeAt: null });
      }
    }
  };

  const start = () =>
    run(() =>
      runAuditExport({ ...callbacks, format: chosenFormat, scope, filters: scope === 'selection' ? filters : {} })
    );

  const resumeAt = status.state === 'stopped' ? status.resumeAt : null;
  // Continuing makes sense once a file is saved, and while the export's pages are still served
  const continuable = resumeAt !== null && resumeAt.files > 1 && resumeAt.file > 1 && canContinue(resumeAt);
  const resume = () => resumeAt && run(() => continueAuditExport(resumeAt, callbacks));

  const blocked = plan?.tier === 'too_large';
  const formatCount = (n: number | null) => (n === null ? 'counting…' : `${formatEvents(n)} events`);

  return (
    <div className="modal-overlay" onClick={running ? undefined : onClose}>
      <div className="modal audit-export-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Export audit logs</h2>

        <fieldset className="audit-export-options" disabled={running}>
          <legend>Events</legend>
          <label className="audit-export-option">
            <input type="radio" name="audit-export-scope" checked={scope === 'all'} onChange={() => setScope('all')} />
            <span>
              All history <span className="audit-muted">({formatCount(allCount)})</span>
            </span>
          </label>
          <label className={`audit-export-option ${hasFilters ? '' : 'disabled'}`}>
            <input
              type="radio"
              name="audit-export-scope"
              checked={scope === 'selection'}
              disabled={!hasFilters}
              onChange={() => setScope('selection')}
            />
            <span>
              Selected data{' '}
              {hasFilters ? (
                <span className="audit-muted">({formatCount(selectionCount)})</span>
              ) : (
                <span className="audit-muted">(no filter selected)</span>
              )}
              {hasFilters && (
                <ul className="audit-export-filters">
                  {filterSummary.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
            </span>
          </label>
        </fieldset>

        <fieldset className="audit-export-options" disabled={running}>
          <legend>Format</legend>
          <label className="audit-export-option">
            <input type="radio" name="audit-export-format" checked={chosenFormat === 'csv'} onChange={() => setFormat('csv')} />
            <span>CSV (.csv)</span>
          </label>
          <label className={`audit-export-option ${plan && !plan.excelAllowed ? 'disabled' : ''}`}>
            <input
              type="radio"
              name="audit-export-format"
              checked={chosenFormat === 'xlsx'}
              disabled={plan !== null && !plan.excelAllowed}
              onChange={() => setFormat('xlsx')}
            />
            <span>
              Excel (.xlsx)
              {plan && !plan.excelAllowed && (
                <span className="audit-muted"> (up to {formatEvents(EVENTS_PER_FILE)} events)</span>
              )}
            </span>
          </label>
        </fieldset>

        {plan && count !== null && (status.state === 'idle' || (status.state === 'stopped' && !continuable)) && (
          <VolumeNotice plan={plan} events={count} />
        )}

        {status.state === 'running' && (
          <div className="audit-export-progress" role="status">
            <div className="audit-export-progress-bar">
              <div
                style={{
                  width: `${status.progress ? Math.min(100, (status.progress.fetched / Math.max(status.progress.total, 1)) * 100) : 0}%`,
                }}
              />
            </div>
            {status.progress ? (
              <>
                {status.progress.files > 1 && `File ${status.progress.file} of ${status.progress.files} · `}
                {formatEvents(status.progress.fetched)} of {formatEvents(status.progress.total)} events
                {status.progress.secondsLeft !== null && ` · ${describeTimeLeft(status.progress.secondsLeft)}`}
              </>
            ) : (
              'Starting…'
            )}
            <div className="audit-muted">Keep this tab open until the export finishes.</div>
          </div>
        )}

        {status.state === 'stopped' && (
          <div className="error-message">
            <div>{status.message}</div>
            {resumeAt && resumeAt.files > 1 && resumeAt.file > 1 && (
              <div>
                Files already saved: {resumeAt.file - 1} of {resumeAt.files}.
                {!continuable && ' Its pages are no longer served: start over to export the rest.'}
              </div>
            )}
            {status.cancelled && !continuable && <div>It still appears in the audit log, as it was started.</div>}
          </div>
        )}

        <div className="modal-actions">
          {running ? (
            <button type="button" className="btn-secondary" onClick={() => (cancelled.current = true)}>
              Cancel export
            </button>
          ) : (
            <>
              <button type="button" className="btn-secondary" onClick={onClose}>
                Close
              </button>
              {continuable && resumeAt ? (
                <>
                  <button type="button" className="btn-secondary" onClick={start} disabled={blocked}>
                    Start over
                  </button>
                  <button type="button" className="btn-primary" onClick={resume}>
                    Continue from file {resumeAt.file} of {resumeAt.files}
                  </button>
                </>
              ) : (
                <button type="button" className="btn-primary" onClick={start} disabled={blocked}>
                  {status.state === 'stopped' ? 'Try again' : plan && plan.files > 1 && !blocked ? `Export ${plan.files} files` : 'Export'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** What the chosen export amounts to, worded for its size. */
function VolumeNotice({ plan, events }: { plan: ReturnType<typeof planExport>; events: number }) {
  const size = describeSize(plan.bytes);
  const duration = describeDuration(plan.seconds.min, plan.seconds.max);
  const formatName = plan.format === 'csv' ? 'CSV' : 'Excel';

  switch (plan.tier) {
    case 'too_large':
      return (
        <div className="audit-export-notice blocked" role="alert">
          {formatEvents(events)} events are more than one export can hold ({formatEvents(MAX_EXPORT_EVENTS)}). Narrow the
          selection, for example with a date range on the Time column, or contact Fakto support for a complete archive.
        </div>
      );
    case 'multi_file':
      return (
        <div className="audit-export-notice warning">
          <strong>
            {formatEvents(events)} events will be saved as {plan.files} CSV files
          </strong>{' '}
          of up to {formatEvents(EVENTS_PER_FILE)} events each (about {size} in total, {duration}). Spreadsheets open at most
          about a million rows per file. Each file is saved as soon as it is ready, and your browser may ask you to allow
          multiple downloads. Keep this tab open until the last one is saved.
        </div>
      );
    case 'large':
      return (
        <div className="audit-export-notice warning">
          <strong>Large export:</strong> {formatEvents(events)} events, about {size} as {formatName}, {duration}. Keep
          this tab open until it finishes. To export less, add a date range or other filters.
        </div>
      );
    default:
      return (
        <p className="audit-export-notice hint">
          About {size} · {duration}. Events are listed oldest first; the export is recorded in the audit log, as the
          file&apos;s last line.
        </p>
      );
  }
}
